// History's calendar as rules (plan-progress.md "History's calendar"):
// which month is "this month" in the person's zone, a month's bounds as
// the API's from/to days, its grid (Sunday first), sessions grouped by the
// day they started in that zone, and each day's name as a screen reader
// hears it. Pure — pinned by calendar.test.js; HistoryCalendar lays it out.
//
// A month is "YYYY-MM" and a day "YYYY-MM-DD": calendar dates, not
// instants. The grid's arithmetic runs on those dates in UTC (Date.UTC),
// so no zone's daylight-saving shift can skip or repeat a day. The person's
// zone enters in two places only: what today is (`monthOf`) and which day a
// session started on (`sessionsByDay`).
import { dayKey } from "./format";
import { stateBadge } from "./history";

// backend/training/views.py SessionPagination.max_page_size: a month is
// read 100 sessions at a time, page after page.
export const MONTH_PAGE_SIZE = 100;
// A runaway guard, not a real limit (hooks/utils.js MAX_PAGES' reasoning):
// 500 sessions in one month. Hitting it is said on screen, never hidden.
export const MONTH_MAX_PAGES = 5;

const MONTH_RE = /^(\d{4})-(\d{2})$/;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
// Date.UTC reads a year below 100 as 19xx; the calendar keeps to four digits.
const MIN_YEAR = 1900;

const pad = (n) => String(n).padStart(2, "0");
const split = (month) => month.split("-").map(Number);

/** "2026-10" when `raw` is a month the calendar can show, else null. */
export function parseMonth(raw) {
  const match = typeof raw === "string" ? MONTH_RE.exec(raw) : null;
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  return year >= MIN_YEAR && month >= 1 && month <= 12 ? raw : null;
}

/** The month it is at `now` in `timeZone` ("2026-10"): the calendar's *Today*. */
export const monthOf = (now, timeZone) => dayKey(now, timeZone).slice(0, 7);

/** How many days `month` has (February 29 in a leap year). */
export function daysInMonth(month) {
  const [year, m] = split(month);
  return new Date(Date.UTC(year, m, 0)).getUTCDate(); // day 0 of the next month
}

/** `month` moved by `delta` months: "2026-12" + 1 → "2027-01". */
export function shiftMonth(month, delta) {
  const [year, m] = split(month);
  const date = new Date(Date.UTC(year, m - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;
}

/**
 * The month's first and last day as sessions/' `from` / `to` — days the
 * server reads in the person's zone, both inclusive.
 */
export const monthBounds = (month) => ({
  from: `${month}-01`,
  to: `${month}-${pad(daysInMonth(month))}`,
});

/** A day from the URL ("2026-10-06") when it is a real day of `month`, else null. */
export function parseDay(raw, month) {
  const match = typeof raw === "string" ? DAY_RE.exec(raw) : null;
  if (!match || raw.slice(0, 7) !== month) return null;
  const day = Number(match[3]);
  return day >= 1 && day <= daysInMonth(month) ? raw : null;
}

/**
 * The month as weeks, Sunday first: each week is 7 cells, a day
 * ("2026-10-01") or null where the week runs into the next or last month.
 */
export function monthWeeks(month) {
  const [year, m] = split(month);
  const lead = new Date(Date.UTC(year, m - 1, 1)).getUTCDay(); // 0 = Sunday
  const cells = Array.from({ length: lead }, () => null);
  const days = daysInMonth(month);
  for (let day = 1; day <= days; day += 1) cells.push(`${month}-${pad(day)}`);
  while (cells.length % 7) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/**
 * Sessions by the day they started in `timeZone`: Map("YYYY-MM-DD" → rows),
 * each day's rows in the order they started — a day reads morning to
 * evening, though sessions/ answers newest first (that order is left alone
 * for the read, so a cut-short month keeps the newest). Ties keep the order
 * given.
 */
export function sessionsByDay(rows, timeZone) {
  const byDay = new Map();
  for (const row of rows ?? []) {
    const day = dayKey(row?.started_at, timeZone);
    if (!day) continue;
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push(row);
  }
  for (const dayRows of byDay.values()) {
    dayRows.sort((a, b) => Date.parse(a.started_at) - Date.parse(b.started_at));
  }
  return byDay;
}

// The order a day's markers and its name list the states in.
const STATE_ORDER = ["finished", "auto_closed", "open"];

/**
 * A day's sessions counted by state, in History's order (finished, closed
 * itself, open; an unknown state after them, as words):
 * `[{state, label, count}]` with labels lower-case ("closed itself").
 */
export function stateCounts(rows) {
  const counts = new Map();
  for (const row of rows ?? []) {
    const state = row?.session_state ?? "";
    counts.set(state, (counts.get(state) ?? 0) + 1);
  }
  const rank = (state) => {
    const index = STATE_ORDER.indexOf(state);
    return index === -1 ? STATE_ORDER.length : index;
  };
  return [...counts.entries()]
    .sort(([a], [b]) => rank(a) - rank(b)) // stable: unknown states keep their first-seen order
    .map(([state, count]) => ({ state, label: stateBadge(state).label.toLowerCase(), count }));
}

// A calendar date, read at noon UTC and formatted in UTC: the same date in
// every zone, whatever the browser's.
const atNoon = (day) => {
  const [year, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(year, m - 1, d, 12));
};

/** "Tuesday, October 6". */
export const longDay = (day) =>
  atNoon(day).toLocaleDateString(undefined, {
    timeZone: "UTC",
    weekday: "long",
    month: "long",
    day: "numeric",
  });

/** "October 2026". */
export const monthLabel = (month) =>
  atNoon(`${month}-01`).toLocaleDateString(undefined, {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  });

/** The day of the month as shown in its cell: "6". */
export const dayNumber = (day) => String(Number(day.slice(8, 10)));

/** The week's columns, Sunday first: `[{short: "Sun", long: "Sunday"}, …]`. */
export function weekdays() {
  // 2026-01-04 was a Sunday.
  return Array.from({ length: 7 }, (_, i) => {
    const date = atNoon(`2026-01-${pad(4 + i)}`);
    return {
      short: date.toLocaleDateString(undefined, { timeZone: "UTC", weekday: "short" }),
      long: date.toLocaleDateString(undefined, { timeZone: "UTC", weekday: "long" }),
    };
  });
}

const sessionsWord = (n) => `${n} ${n === 1 ? "session" : "sessions"}`;

/**
 * A day's name as its button says it: "Tuesday, October 6: 1 session,
 * finished"; several in one state: "…: 2 sessions, finished"; mixed:
 * "…: 3 sessions (2 finished, 1 open)"; none: "…: no sessions".
 */
export function dayName(day, rows) {
  const counts = stateCounts(rows);
  const total = counts.reduce((sum, { count }) => sum + count, 0);
  const head = longDay(day);
  if (total === 0) return `${head}: no sessions`;
  if (counts.length === 1) return `${head}: ${sessionsWord(total)}, ${counts[0].label}`;
  const parts = counts.map(({ count, label }) => `${count} ${label}`).join(", ");
  return `${head}: ${sessionsWord(total)} (${parts})`;
}

/** "1 session in October 2026." / "4 sessions on 3 days in October 2026." */
export function monthSummary(month, sessions, days) {
  const where = `in ${monthLabel(month)}.`;
  return days > 1 ? `${sessionsWord(sessions)} on ${days} days ${where}` : `${sessionsWord(sessions)} ${where}`;
}

/** `?view=` as History's mode: "calendar", else the list (the default). */
export const historyView = (raw) => (raw === "calendar" ? "calendar" : "list");

/** The calendar's query: `?view=calendar&month=2026-10` (`&day=2026-10-06` when one is chosen). */
export function calendarSearch({ month, day }) {
  const params = new URLSearchParams({ view: "calendar", month });
  if (day) params.set("day", day);
  return params;
}
